import React from "react";
import { formatMoney } from "../lib/format";
import { useSettings } from "../SettingsContext";

const MS_DAY = 24 * 60 * 60 * 1000;

// Compute the next upcoming due date, rolling forward by recurrence if the stored date has passed.
function computeNextDue(account, today) {
  if (!account.payment_due_date) return null;
  const next = new Date(account.payment_due_date + "T00:00:00");
  if (isNaN(next.getTime())) return null;

  const rec = account.payment_recurrence || "Monthly";
  if (rec === "One-time") {
    return next >= today ? next : null;
  }

  let guard = 0;
  while (next < today && guard < 1200) {
    if (rec === "Weekly") next.setDate(next.getDate() + 7);
    else if (rec === "Monthly") next.setMonth(next.getMonth() + 1);
    else if (rec === "Quarterly") next.setMonth(next.getMonth() + 3);
    else if (rec === "Yearly") next.setFullYear(next.getFullYear() + 1);
    else break;
    guard++;
  }
  return next;
}

const DueSoon = ({ accounts, invoices = [], profiles = [], onSelect }) => {
  const { settings } = useSettings();
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const horizon = new Date(today.getTime() + 7 * MS_DAY);

  const profileNameById = {};
  profiles.forEach((p) => {
    profileNameById[p.id] = p.name;
  });

  const accountById = {};
  accounts.forEach((a) => {
    accountById[a.id] = a;
  });

  // Statement-mode and invoice-mode accounts BOTH surface account-level payment
  // due dates in Due Soon. Invoice-mode accounts additionally get one row per
  // unpaid invoice further below (each with its own due_date).

  // Upcoming payment items (existing behavior)
  const paymentItems = accounts
    .filter((account) => !account.is_paid)
    .map((account) => ({ kind: "payment", account, when: computeNextDue(account, new Date(today)) }))
    .filter((x) => x.when && x.when >= today && x.when <= horizon)
    .map((x) => ({ ...x, overdue: false }));

  // Overdue payment items: any unpaid account whose ORIGINAL due date is in the past.
  // computeNextDue rolls the date forward, so we compare against the raw stored date
  // (rolled back by one recurrence step from the next due) to know how many days late.
  const overduePaymentItems = accounts
    .filter((account) => !account.is_paid && account.payment_due_date)
    .map((account) => {
      const next = computeNextDue(account, new Date(today));
      if (!next) return null;
      // The most-recent past due date is next minus one recurrence step.
      const past = new Date(next);
      const rec = account.payment_recurrence || "Monthly";
      if (rec === "Weekly") past.setDate(past.getDate() - 7);
      else if (rec === "Monthly") past.setMonth(past.getMonth() - 1);
      else if (rec === "Quarterly") past.setMonth(past.getMonth() - 3);
      else if (rec === "Yearly") past.setFullYear(past.getFullYear() - 1);
      else return null;
      // Only include if the past date is actually before the account's stored due date's
      // original month — i.e., the account has ever had a passed cycle.
      const stored = new Date(account.payment_due_date + "T00:00:00");
      if (isNaN(stored.getTime()) || past < stored) return null;
      return past >= today ? null : { kind: "payment", account, when: past, overdue: true };
    })
    .filter(Boolean);

  // Invoice-mode items: each unpaid invoice with a due_date contributes a row.
  const invoiceUpcoming = [];
  const invoiceOverdue = [];
  invoices.forEach((inv) => {
    if (inv.is_paid) return;
    if (!inv.due_date) return;
    const when = new Date(inv.due_date + "T00:00:00");
    if (isNaN(when.getTime())) return;
    const account = accountById[inv.account_id];
    if (!account) return;
    if (when < today) {
      invoiceOverdue.push({ kind: "payment", account, invoice: inv, when, overdue: true });
    } else if (when <= horizon) {
      invoiceUpcoming.push({ kind: "payment", account, invoice: inv, when, overdue: false });
    }
  });

  // Overdue and upcoming (next 7 days) Contract requirement items
  const requirementItems = [];
  accounts.forEach((account) => {
    if (account.account_type !== "Contract") return;
    (account.requirements || []).forEach((req) => {
      if (req.completed || !req.due_date) return;
      const when = new Date(req.due_date + "T00:00:00");
      if (isNaN(when.getTime())) return;
      if (when >= today && when <= horizon) {
        requirementItems.push({ kind: "requirement", account, req, when, overdue: false });
      } else if (when < today) {
        requirementItems.push({ kind: "requirement", account, req, when, overdue: true });
      }
    });
  });

  const items = [...paymentItems, ...overduePaymentItems, ...invoiceUpcoming, ...invoiceOverdue, ...requirementItems].sort((a, b) => a.when - b.when);

  if (items.length === 0) return null;

  const daysUntil = (d) => Math.round((d - today) / MS_DAY);

  const overdueCount = overduePaymentItems.length + invoiceOverdue.length + requirementItems.filter((r) => r.overdue).length;
  const upcomingReqCount = requirementItems.length - requirementItems.filter((r) => r.overdue).length;
  const upcomingCount = paymentItems.length + invoiceUpcoming.length + upcomingReqCount;

  return (
    <div className="mb-8" data-testid="due-soon-widget">
      <div className="bg-white rounded-lg shadow overflow-hidden border-l-4 border-amber-400">
        <div className="px-4 py-2 border-b border-gray-200 bg-[#e1e2e4] flex items-center justify-between">
          <h3 className="text-sm font-semibold text-gray-900">Due Soon &amp; Overdue</h3>
          <span className="text-xs text-gray-500" data-testid="due-soon-count">
            {upcomingCount > 0 && `${upcomingCount} upcoming`}
            {upcomingCount > 0 && overdueCount > 0 && " · "}
            {overdueCount > 0 && `${overdueCount} overdue`}
          </span>
        </div>
        <div className="px-2 py-1 divide-y divide-gray-100">
          {items.map((item) => {
            if (item.kind === "requirement") {
              const { account, req, when, overdue } = item;
              let label;
              if (overdue) {
                const daysAgo = -daysUntil(when);
                label = daysAgo === 0 ? "Today" : daysAgo === 1 ? "1 day overdue" : `${daysAgo} days overdue`;
              } else {
                const d = daysUntil(when);
                label = d === 0 ? "Today" : d === 1 ? "Tomorrow" : `in ${d} days`;
              }
              const pillClass = overdue
                ? "bg-red-100 text-red-700"
                : "bg-amber-100 text-amber-800";
              const rowHover = overdue ? "hover:bg-red-50" : "hover:bg-amber-50";
              return (
                <button
                  key={`req-${req.id}`}
                  onClick={() => onSelect(account)}
                  data-testid={`due-soon-req-${req.id}`}
                  className={`w-full flex items-center justify-between text-left px-3 py-1.5 rounded-md ${rowHover} transition-colors`}
                >
                  <div className="min-w-0">
                    {profileNameById[account.profile_id] && (
                      <span
                        className="mr-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-700"
                        data-testid={`due-soon-req-profile-${req.id}`}
                      >
                        {profileNameById[account.profile_id]}
                      </span>
                    )}
                    <span className="text-sm font-medium text-gray-900">{req.description}</span>
                    <span className={`text-xs ml-2 font-medium ${overdue ? "text-red-600" : "text-amber-700"}`}>Requirement</span>
                    <span className="text-xs text-gray-500 ml-2">· {account.name}</span>
                  </div>
                  <div className="flex items-center space-x-3 flex-shrink-0">
                    <span className="text-sm text-gray-700">
                      {when.toLocaleDateString(undefined, { month: "short", day: "numeric" })}
                    </span>
                    <span className={`text-xs font-medium px-2 py-1 rounded-full ${pillClass}`}>
                      {label}
                    </span>
                  </div>
                </button>
              );
            }
            const { account, when, overdue, invoice } = item;
            const d = daysUntil(when);
            let label;
            if (overdue) {
              const daysAgo = -d;
              label = daysAgo === 0 ? "Today" : daysAgo === 1 ? "1 day overdue" : `${daysAgo} days overdue`;
            } else {
              label = d === 0 ? "Today" : d === 1 ? "Tomorrow" : `in ${d} days`;
            }
            const pillClass = overdue
              ? "bg-red-100 text-red-700"
              : d <= 2
              ? "bg-red-100 text-red-700"
              : "bg-amber-100 text-amber-800";
            const rowHover = overdue ? "hover:bg-red-50" : "hover:bg-amber-50";
            const rowAmount = invoice ? invoice.amount : account.amount_due;
            const testId = invoice
              ? `due-soon-invoice-${invoice.id}`
              : overdue
              ? `due-soon-overdue-${account.id}`
              : `due-soon-${account.id}`;
            return (
              <button
                key={invoice ? `inv-${invoice.id}` : `${overdue ? "ov" : "up"}-${account.id}`}
                onClick={() => onSelect(account)}
                data-testid={testId}
                className={`w-full flex items-center justify-between text-left px-3 py-1.5 rounded-md ${rowHover} transition-colors`}
              >
                <div className="min-w-0">
                  {profileNameById[account.profile_id] && (
                    <span
                      className="mr-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-700"
                      data-testid={`due-soon-profile-${account.id}`}
                    >
                      {profileNameById[account.profile_id]}
                    </span>
                  )}
                  <span className="text-sm font-medium text-gray-900">{account.name}</span>
                  {account.auto_pay && (
                    <span
                      className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-800"
                      data-testid={`due-soon-auto-pay-${invoice ? invoice.id : account.id}`}
                      title="Automatic withdrawal — will be marked paid when the due date passes"
                    >
                      Auto Pay
                    </span>
                  )}
                  {invoice && (
                    <span className="text-xs text-blue-700 ml-2 font-medium">
                      Invoice{invoice.invoice_number ? ` #${invoice.invoice_number}` : ""}
                    </span>
                  )}
                  <span className="text-xs text-gray-500 ml-2">{account.account_type}</span>
                  {!invoice && account.payment_recurrence && account.payment_recurrence !== "One-time" && (
                    <span className="text-xs text-gray-400 ml-2">· {account.payment_recurrence}</span>
                  )}
                </div>
                <div className="flex items-center space-x-3 flex-shrink-0">
                  {rowAmount != null && (
                    <span className="text-sm font-semibold text-gray-900" data-testid={`due-soon-amount-${invoice ? invoice.id : account.id}`}>
                      {formatMoney(rowAmount, settings)}
                    </span>
                  )}
                  <span className="text-sm text-gray-700">
                    {when.toLocaleDateString(undefined, { month: "short", day: "numeric" })}
                  </span>
                  <span
                    className={`text-xs font-medium px-2 py-1 rounded-full ${pillClass}`}
                  >
                    {label}
                  </span>
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default DueSoon;
